import Storage from 'utils/localStorage/Storage';

const LYRA_CACHE_TTL = Storage.DAYS * 1; // localStorage wrapper TTL — safety net to auto-expire the entire cache
const LYRA_KEY_TTL_MS = 24 * 60 * 60 * 1000; // per-campaign-key TTL — used to filter stale keys on read

const KEYS = {
    LYRA_ADVICES_BY_CAMPAIGN: 'LYRA_ADVICES_BY_CAMPAIGN'
};

const isLyraRankingEnabled = () => {
    return Sephora.configurationSettings?.lyraRanking?.isEnabled === true;
};

/**
 * Storage format (per-key TTL):
 * {
 *   "entrypoint_prompts": { "advices": ["uuid-1"], "savedAt": 1710700000000 },
 *   "another_campaign": { "advices": ["uuid-2"], "savedAt": 1710600000000 }
 * }
 */

const getRawCache = () => {
    try {
        const cached = Storage.local.getItem(KEYS.LYRA_ADVICES_BY_CAMPAIGN);

        if (!cached) {
            return null;
        }

        return JSON.parse(cached);
    } catch (error) {
        // Remove potentially corrupted cache entry to avoid repeated JSON.parse errors
        Storage.local.removeItem(KEYS.LYRA_ADVICES_BY_CAMPAIGN);
        Sephora.logger.error('Lyra: Error reading lyra_advices_by_campaign from cache', error);

        return null;
    }
};

const getLyraAdvicesByCampaign = () => {
    const raw = getRawCache();

    if (!raw) {
        return null;
    }

    const now = Date.now();
    const result = {};

    Object.keys(raw).forEach(key => {
        const entry = raw[key];

        if (entry && entry.savedAt && now - entry.savedAt < LYRA_KEY_TTL_MS) {
            result[key] = entry.advices;
        }
    });

    return Object.keys(result).length > 0 ? result : null;
};

const mergeLyraAdvice = lyraAdvice => {
    if (!isLyraRankingEnabled()) {
        return;
    }

    if (!lyraAdvice || typeof lyraAdvice !== 'object') {
        return;
    }

    try {
        const raw = getRawCache() || {};
        const now = Date.now();

        const pruned = {};

        Object.keys(raw).forEach(key => {
            const entry = raw[key];

            if (entry && entry.savedAt && now - entry.savedAt < LYRA_KEY_TTL_MS) {
                pruned[key] = entry;
            }
        });

        Object.keys(lyraAdvice).forEach(key => {
            pruned[key] = { advices: lyraAdvice[key], savedAt: now };
        });

        Storage.local.setItem(KEYS.LYRA_ADVICES_BY_CAMPAIGN, JSON.stringify(pruned), LYRA_CACHE_TTL);
    } catch (error) {
        Sephora.logger.error('Lyra: Error merging lyra_advice into cache', error);
    }
};

const buildLyraAdviceField = () => {
    if (!isLyraRankingEnabled()) {
        return null;
    }

    const advices = getLyraAdvicesByCampaign();

    if (!advices) {
        return null;
    }

    try {
        return JSON.stringify(advices);
    } catch {
        return null;
    }
};

const clearLyraAdviceCache = () => {
    Storage.local.removeItem(KEYS.LYRA_ADVICES_BY_CAMPAIGN);
};

export {
    isLyraRankingEnabled, mergeLyraAdvice, getLyraAdvicesByCampaign, buildLyraAdviceField, clearLyraAdviceCache, KEYS
};
